import Model from '@/lib/data/Model';
import FormQuestionData from '@/data/FormQuestionData';
import FormQuestionControlData from '@/data/FormQuestionControlData';
import FormQuestionHelpDocument from '@/data/FormQuestionHelpDocument';
import Translation from './Translation';

const QUESTION_URL = '/forms/form/question';

class FormQuestion extends Model {
    static idProperty = 'id';

    visible = true;

    fields = [
        'id',
        'caption',
        { name: 'control_name', defaultValue: '' },
        { name: 'control_type', defaultValue: '' },
        'control_type_id',
        'default_value',
        'help_text',
        'min_count',
        'max_count',
        'max_limit',
        { name: 'mandatory', type: 'bool', defaultValue: false },
        { name: 'show_in_table', type: 'bool', defaultValue: true },
        { name: 'is_lazy_loaded', type: 'bool', defaultValue: false },
        'test_id',
        'tag',
        'score_weight',
        { name: 'is_used_for_score', type: 'bool', defaultValue: false },
        'form_id',
        'form_ques_section_id',
        'order',
        {
            name: 'control_data',
            association: {
                type: 'many',
                model: FormQuestionControlData,
            },
        },
        {
            name: 'help_docs',
            association: {
                type: 'many',
                model: FormQuestionHelpDocument,
            },
        },
        {
            name: 'data',
            association: {
                type: 'one',
                model: FormQuestionData,
            },
        },
        {
            name: 'translations',
            association: {
                type: 'many',
                model: Translation,
            },
        },
        {
            name: 'translation',
            association: {
                type: 'one',
                model: Translation,
            },
        },
        'sort_by',
        { name: 'default_visibility', type: 'bool', defaultValue: true },
        { name: 'is_add_new_possible', type: 'bool', defaultValue: false },
        'control_tag',
        'ai_ques_id',
        'calc_method',
        'ques_comment',
        { name: 'show_add_ri', type: 'bool', defaultValue: false },
        { name: 'factors', type: 'json', defaultValue: [] },
        /* isDownloadComponent component icin kullaniliyor */
        /* default_value de file_storage_id tutulacak */
        { name: 'file' },
    ];

    constructor(data) {
        super(data);
        this.init();
    }

    get isMedia() {
        return this.isImage || this.isAudio || this.isVideo;
    }

    get isListType() {
        return (
            this.isComboBox ||
            this.isComboBoxAsCombo ||
            this.isCheckBoxList ||
            this.isNCBListType ||
            this.isNathanType
        );
    }

    get allowCreate() {
        return this.is_add_new_possible;
    }

    get isNCBUNID() {
        return this.isControl('NCB_UNID');
    }

    get isNCBVessel() {
        return this.isControl('NCB_Vessel');
    }

    get isNCBOperator() {
        return this.isControl('NCB_Operator');
    }

    get isNCBPreviewCheck() {
        return this.isControl('NCB_PreviewCheck');
    }

    get isNCBListType() {
        return (
            this.isNCBType &&
            !this.isNCBValidatorType &&
            !this.isNCBPreviewCheck &&
            !this.isNCBSegrationCheck &&
            !this.isNCBPlacard
        );
    }

    get isNCBType() {
        return this.control_name.indexOf('NCB_') > -1;
    }

    get isNCBValidatorType() {
        return this.isControl('NCB_Container Number');
    }

    get isNCBSegrationCheck() {
        return this.isControl('NCB_SegregationCheck');
    }

    get isNCBPlacard() {
        return this.isControl('NCB_Placard');
    }

    get isNathanType() {
        return this.control_name.indexOf('Nathan') >= 0;
    }

    get isYachtType() {
        return this.isControl('Yacht');
    }

    get isLabel() {
        return this.isControl('Label');
    }

    get isLabelImage() {
        return this.isControl('LabelImage');
    }

    get isPageBreak() {
        return this.isControl('PageBreak');
    }

    get isNumber() {
        return !this.isNCBValidatorType && this.isControl('Number');
    }

    get isMoney() {
        return this.isControl('Money');
    }

    get isCoordinate() {
        return this.isControl('Coordinate');
    }

    get isDateMonth() {
        return this.isControl('DateMonth');
    }

    get isDate() {
        return this.isControl('Date');
    }

    get isTime() {
        return this.isControl('Time');
    }

    get isComboBoxAsCombo() {
        return this.isControl('ComboBoxAsCombo');
    }

    get isComboBox() {
        return this.isControl('ComboBox');
    }

    get isCheckBoxList() {
        return this.isControl('CheckBoxList');
    }

    get isYesNo() {
        return this.isControl('YesNo');
    }

    get isImage() {
        return this.isControl('Image');
    }

    get isVideo() {
        return this.isControl('Video');
    }

    get isAudio() {
        return this.isControl('Audio');
    }

    get isSignature() {
        return this.isControl('Signature');
    }

    get isTextBox() {
        return this.isControl('TextBox');
    }

    get isTextArea() {
        return this.isControl('TextArea');
    }

    get isHtmlEditor() {
        return this.isControl('HtmlEditor');
    }

    get isBuilding() {
        return this.isControl('Building');
    }

    get isRSALossEstimate() {
        return this.isControl('RSA_loss_estimates');
    }

    get isVisitLocationAddress() {
        return this.isControl('VisitLocationAddress');
    }

    get isVisitLocationTradeCode() {
        return this.isControl('VisitLocationTradeCode');
    }

    get isQRA() {
        return this.isControl('QRA_IH') || this.isControl('QRA_RI');
    }

    get isQRARI() {
        return this.isControl('QRA_RI');
    }

    get isQRAIH() {
        return this.isControl('QRA_IH');
    }

    get isSICComponent() {
        return this.isControl('SICComponent');
    }

    get isDownloadComponent() {
        return this.isControl('DownloadComponent');
    }

    isControl(controlType) {
        const re = new RegExp(`${controlType}$`, 'gi');
        return re.test(this.control_name);
    }

    api = [
        {
            name: 'create',
            url: QUESTION_URL,
            associated: true,
        },
        {
            name: 'update',
            url: QUESTION_URL,
            associated: true,
        },
        {
            name: 'destroy',
            url: QUESTION_URL,
            method: 'DELETE',
        },
        {
            name: 'getUNIDInfo',
            url: `${QUESTION_URL}/unidinfo`,
            method: 'GET',
        },
        {
            name: 'getControlDataList',
            url: `${QUESTION_URL}/controldatalist`,
            method: 'GET',
        },
        {
            name: 'getControlData',
            url: `${QUESTION_URL}/controldata`,
            method: 'GET',
        },
        {
            name: 'checkSegregation',
            url: `${QUESTION_URL}/apicall`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        },
        {
            name: 'getPlacards',
            url: `${QUESTION_URL}/apicall`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        },
    ];
}

export default FormQuestion;
